import {
  headingId,
  imageKey,
  isSupportingMark,
} from "../../lib/content-blocks";
import { imageProps, localHref } from "../../lib/links";
import { techFor } from "../../data/tech";
import RichText from "./RichText";
import TechChip from "./TechChip";

// Page heroes own the h1, so captured h1s are dropped and deep levels collapse to h3.
const headingTags = { h2: "h2", h3: "h3", h4: "h4", h5: "h3", h6: "h3" };

function MarkRow({ marks, title, tech }) {
  return (
    <div className="mark-row">
      {marks.map((block) =>
        tech && techFor(block.src) ? (
          <TechChip key={block.src} tech={techFor(block.src)} />
        ) : (
          <figure key={block.src} className="client-logo">
            <img
              {...imageProps(localHref(block.src), "280px")}
              alt={block.alt || `${title} logo`}
              loading="lazy"
            />
          </figure>
        ),
      )}
    </div>
  );
}

/**
 * Captured page body blocks as prose. Heading ids include the block index, so
 * anything linking to them (e.g. a table of contents) must index the same array.
 * Repeated images (the live pages often carry desktop and mobile copies) show
 * once, consecutive logos are grouped into one row of tiles, and consecutive
 * list items into one list. With `tech`
 * (a Technology Stacks section), recognised technology logos become chips.
 */
export default function ContentBlocks({
  blocks = [],
  title,
  skipImages = false,
  tech = false,
}) {
  const seen = new Set();
  const out = [];
  let marks = [];
  let items = [];
  // Captured list items arrive one block each; consecutive ones form a list.
  const flushItems = () => {
    if (items.length)
      out.push(
        <ul key={`list-${out.length}`}>
          {items.map((item) => (
            <li key={item.i}>{item.text}</li>
          ))}
        </ul>,
      );
    items = [];
  };
  const flushMarks = () => {
    if (marks.length)
      out.push(
        <MarkRow
          key={`marks-${out.length}`}
          marks={marks}
          title={title}
          tech={tech}
        />,
      );
    marks = [];
  };

  blocks.forEach((block, i) => {
    if (block.type === "li") {
      flushMarks();
      items.push({ i, text: block.text });
      return;
    }
    flushItems();
    if (block.type === "image") {
      if (skipImages || seen.has(imageKey(block.src))) return;
      seen.add(imageKey(block.src));
      if (isSupportingMark(block.src) || (tech && techFor(block.src))) {
        marks.push(block);
        return;
      }
    }
    flushMarks();
    switch (block.type) {
      case "h1":
      case "embed":
        return;
      case "image":
        out.push(
          <figure key={i}>
            <img
              {...imageProps(
                localHref(block.src),
                "(max-width: 850px) 100vw, 760px",
              )}
              alt={block.alt || `${title} — supporting image`}
              loading="lazy"
            />
          </figure>,
        );
        return;
      case "rich":
        out.push(<RichText key={i} html={block.html} />);
        return;
      case "blockquote":
        out.push(<blockquote key={i}>{block.text}</blockquote>);
        return;
    }
    const Heading = headingTags[block.type];
    out.push(
      Heading ? (
        <Heading key={i} id={headingId(block.text, i)}>
          {block.text}
        </Heading>
      ) : (
        <p key={i}>{block.text}</p>
      ),
    );
  });
  flushItems();
  flushMarks();

  return <div className="prose-content">{out}</div>;
}
